import React from "react";
import styled from "styled-components";
import { theme } from "../theme/semanticTheme";

const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.background};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.fontSize.sm};
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundAlt};
  border-bottom: 1px solid ${theme.colors.border};
`;

const Td = styled.td`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
  color: ${theme.colors.text};

  &:last-child {
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const Tr = styled.tr`
  background: ${(p) => (p.$striped && p.$index % 2 === 1 ? theme.colors.backgroundAlt : theme.colors.background)};
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.base};
`;

const EmptyWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.base};
`;

/**
 * DataTable – simple table with columns, rows, optional loading and empty state.
 * For sortable/paginated/row-actions use the full Table component from design-system/Table.
 *
 * @param {{ columns: { key: string, label: string }[], rows: object[], striped?: boolean, loading?: boolean, loadingMessage?: string, emptyMessage?: string }} props
 */
export default function DataTable({
  columns = [],
  rows = [],
  striped = true,
  loading = false,
  loadingMessage = "Loading...",
  emptyMessage = "No data",
}) {
  if (loading) {
    return (
      <TableWrap>
        <LoadingWrap>{loadingMessage}</LoadingWrap>
      </TableWrap>
    );
  }

  if (!rows.length) {
    return (
      <TableWrap>
        <EmptyWrap>{emptyMessage}</EmptyWrap>
      </TableWrap>
    );
  }

  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            {columns.map((col) => (
              <Th key={col.key}>{col.label ?? col.key}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <Tr key={i} $striped={striped} $index={i}>
              {columns.map((col) => (
                <Td key={col.key}>{row[col.key] ?? "–"}</Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}
